// Ngăn chi tiết một cấp dưới trên sơ đồ mạng lưới của Đại lý / CTV (/network). Đầu ngăn (loại, trạng thái, việc cần
// xử lý) · đường dẫn · Đơn 30 ngày / Cấp dưới / Ngày tạo · chủ, SĐT, cấp trên, bảng giá, bảng chiết khấu, giao diện,
// giới thiệu · danh sách cấp dưới · Xem đơn + Gọi ở đáy. Bấm cấp trên / cấp dưới / đường dẫn = chọn đơn vị đó trên cây.
// Việc làm được ngay trong ngăn (docs/KE_HOACH_BANG_GIA_KHI_TAO_DON_VI.md §4.6, §10):
//  · cấp dưới TRỰC TIẾP: Gán / Đổi bảng giá (có sẵn hoặc lập bảng mới); CTV: Gán / Đổi bảng chiết khấu (đổi → từ tháng sau);
//  · Shop TRỰC TIẾP, chỉ chủ đơn vị: Đặt lại mật khẩu chủ Shop, Tạm khóa / Mở khóa (chỉ mở khoá do mình đặt);
//  · Xem đơn: trang Đơn hàng lọc sẵn đơn vị này (?unit=).

import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { networkAncestors, networkCountsText, networkUnitTitle, type NetworkNode, type NetworkUnitBase, type NetworkUnitType } from '@shoptrack/common/network-tree'
import { workModesLabel, type WorkMode } from '@shoptrack/common/unit-permissions'
import { formatPhoneVN } from '@/lib/format'
import { cn } from '@/lib/utils'
import {
  NetworkCrumbs, NetworkPanelBadge, NetworkPanelHead, NetworkPanelPill, NetworkPanelStats, type NetworkCrumb,
} from '@/components/network/NetworkPanel'
import { NetworkTypeBadge, networkTypeMeta } from '@/components/network/NetworkTreeRow'
import { networkDownlineIssues, networkDownlineStatusLabel } from '@/components/network/network-view'
import { AssignPriceDialog } from '@/components/network/AssignPriceDialog'
import { AssignSchemeDialog } from '@/components/commissions/AssignSchemeDialog'
import { NewPriceListDialog, NewSchemeDialog } from '@/components/price-lists/NewListDialog'
import { currentMonthVN, monthLabel, type Scheme } from '@/components/commissions/types'
import { Button } from '@/components/ui/button'
import { confirm } from '@/components/ui/confirm'
import { ResetOwnerPasswordDialog } from '@/components/network/ResetOwnerPasswordDialog'
import { get, post } from '@/lib/api'

/** Một cấp dưới của GET /network/units (apps/api/src/routes/network.ts). */
export interface NetworkUnit extends NetworkUnitBase {
  tenantType: NetworkUnitType
  parentName: string | null
  direct: boolean
  /** active | suspended | locked (đang khoá — lockedAt) */
  status: string
  createdAt: string
  owner: { name: string | null; phone: string | null } | null
  priceList: { code: string; name: string; mine: boolean } | null
  /** CTV trực tiếp (mình trả chiết khấu): bảng chiết khấu tháng này, null = chưa có. Không có ô = không phải việc của mình */
  scheme?: { id: string; code: string; name: string } | null
  /** CTV trực tiếp: bảng đã hẹn đổi từ tháng sau */
  nextScheme?: { id: string; code: string; name: string; effectiveFromMonth: string } | null
  /** Shop trực tiếp: đang tạm khóa do chính mình (mở được) */
  suspendedByParent?: boolean
  /** Shop trực tiếp: đặt lại được mật khẩu chủ (không dùng ở đơn vị khác, không phải tài khoản quản trị) */
  canResetPassword?: boolean
  /** Shop giới thiệu shop — chỉ cấp quản lý thấy (docs/KE_HOACH_SHOP_GIOI_THIEU.md §5) */
  referredBy?: { id: string; name: string } | null
  referredCount?: number
  /** Giao diện cấp trên bật (Shop: ['sell']) */
  workModes?: WorkMode[]
  /** Số đơn 30 ngày (bỏ nháp / đã hủy). */
  /** null: nhân viên không có quyền xem đơn */
  orders30d?: number | null
}

const SEVERITY = { danger: 'bg-danger/10 text-danger', warn: 'bg-warn/10 text-warn' } as const
const fmt = (n: unknown) => (typeof n === 'number' && Number.isFinite(n) ? n.toLocaleString('vi-VN') : '0')
const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' })
const phoneText = (p: string | null | undefined) => (p ? formatPhoneVN(p) : '')

/** Dòng phụ của một cấp dưới (ô cây, hàng cây, danh sách cấp dưới): Shop → SĐT; Đại lý / CTV → số cấp dưới. */
export function downlineSub(node: NetworkNode<NetworkUnit>): string {
  return node.unit.tenantType === 'shop' ? phoneText(node.unit.owner?.phone) || '—' : networkCountsText(node.below)
}

export function DownlinePanel({
  node,
  rootKey,
  rootName,
  onPick,
  onClose,
  inTree,
  compact,
  canPricing = false,
  isOwner = false,
  ordersPath = null,
}: {
  node: NetworkNode<NetworkUnit>
  rootKey: string
  rootName: string
  onPick: (key: string) => void
  onClose: () => void
  inTree: (id: string) => boolean
  compact: boolean
  /** Quyền Bảng giá & chiết khấu cấp dưới — gán / đổi / lập bảng */
  canPricing?: boolean
  /** Chủ đơn vị (không phải nhân viên) — Đặt lại mật khẩu / Tạm khóa Shop cấp dưới */
  isOwner?: boolean
  /** Trang Đơn hàng của chế độ Quản lý ('/agency/orders' | '/ctv/orders'); null = không có quyền xem đơn */
  ordersPath?: string | null
}) {
  const u = node.unit
  const qc = useQueryClient()
  // Hộp đang mở: gán bảng giá / chiết khấu, hoặc lập bảng mới (xong thì mở lại hộp gán với bảng vừa lập)
  const [dlg, setDlg] = useState<null | { kind: 'price' | 'scheme'; preset?: string } | { kind: 'newPrice' | 'newScheme' | 'password' }>(null)
  // Bảng giá: chỉ cấp trên trực tiếp gán / đổi (P1). Chiết khấu: CTV trực tiếp (ô scheme có mặt = mình là bên trả)
  const canSetPrice = canPricing && u.direct
  const canSetScheme = canPricing && u.tenantType === 'ctv' && u.scheme !== undefined
  // Shop trực tiếp, chỉ chủ đơn vị
  const ownShop = isOwner && u.direct && u.tenantType === 'shop'
  const suspendM = useMutation({
    mutationFn: (on: boolean) => post(`/network/units/${encodeURIComponent(u.id)}/${on ? 'suspend' : 'unsuspend'}`),
    onSuccess: (_r, on) => {
      toast.success(on ? `Đã tạm khóa ${u.name}` : `Đã mở khóa ${u.name}`)
      qc.invalidateQueries({ queryKey: ['network-units'] })
    },
    onError: (e) => toast.error((e as Error).message),
  })
  async function askSuspend() {
    const ok = await confirm({ title: `Tạm khóa ${u.name}?`, description: 'Đơn vị không tạo được đơn mới cho tới khi mở khóa.', confirmLabel: 'Tạm khóa', tone: 'danger' })
    if (ok) suspendM.mutate(true)
  }
  const listsQ = useQuery({
    queryKey: ['network-price-lists'],
    queryFn: () => get<{ priceLists: Array<{ id: string; code: string; name: string; kind: string; unitCount: number }> }>('/network/price-lists'),
    enabled: dlg?.kind === 'price',
  })
  const schemesQ = useQuery({
    queryKey: ['commissions', 'schemes', 'active'],
    queryFn: () => get<{ schemes: Scheme[] }>('/commissions/schemes'),
    enabled: dlg?.kind === 'scheme',
  })
  const meta = networkTypeMeta(u.tenantType)
  const status = networkDownlineStatusLabel(u.status)
  const desc = node.below.agency + node.below.ctv + node.below.shop
  const phone = u.owner?.phone ?? null
  const padX = compact ? 'px-4' : 'px-5'

  const crumbs: NetworkCrumb[] = [
    { key: rootKey, label: rootName, onPick: () => onPick(rootKey) },
    ...networkAncestors(node).map((a) => ({ key: a.unit.id, label: networkUnitTitle(a.unit), onPick: () => onPick(a.unit.id) })),
    { key: u.id, label: u.name },
  ]

  return (
    <>
      <NetworkPanelHead
        compact={compact}
        badge={<NetworkPanelBadge short={meta.short} className={cn(meta.tone, 'bg-toneBg text-toneFg')} />}
        title={u.name}
        pills={
          <>
            <NetworkPanelPill className={cn(meta.tone, 'bg-toneBg text-toneFg')}>{u.code ? `${u.code} · ${meta.label}` : meta.label}</NetworkPanelPill>
            <NetworkPanelPill className={u.status === 'active' ? 'bg-ok/10 text-ok' : u.status === 'suspended' || u.status === 'locked' ? SEVERITY.danger : SEVERITY.warn}>
              {status}
            </NetworkPanelPill>
            {networkDownlineIssues(u)
              .filter((i) => i.label !== status)
              .map((i) => (
                <NetworkPanelPill key={i.label} className={SEVERITY[i.severity]}>
                  {i.label}
                </NetworkPanelPill>
              ))}
          </>
        }
        onClose={onClose}
      >
        <NetworkCrumbs items={crumbs} compact={compact} />
      </NetworkPanelHead>

      <div className={cn('flex flex-col gap-4 pb-5 pt-4', padX)}>
        <NetworkPanelStats
          compact={compact}
          items={[
            { label: 'Đơn 30 ngày', value: u.orders30d == null ? '—' : fmt(u.orders30d) },
            { label: 'Cấp dưới', value: u.tenantType === 'shop' ? '—' : fmt(desc) },
            { label: 'Ngày tạo', value: fmtDate(u.createdAt) },
          ]}
        />

        <dl className={cn('grid gap-x-3 gap-y-2.5 text-[13.5px] leading-5', compact ? 'grid-cols-[112px_minmax(0,1fr)]' : 'grid-cols-[156px_minmax(0,1fr)]')}>
          <Field label="Chủ tài khoản">{u.owner?.name ? <span className="text-ink">{u.owner.name}</span> : <span className="text-muted">Chưa có</span>}</Field>
          <Field label="Điện thoại">
            {phone ? (
              <a href={`tel:${phone.replace(/\s/g, '')}`} className="inline-flex min-h-5 items-center font-mono text-accent hover:underline mobile:-my-3 mobile:min-h-11">
                {phoneText(phone)}
              </a>
            ) : (
              <span className="text-muted">—</span>
            )}
          </Field>
          <Field label="Trực thuộc">
            {node.parent ? (
              <PickButton onClick={() => onPick(node.parent!.unit.id)}>{networkUnitTitle(node.parent.unit)}</PickButton>
            ) : node.orphan ? (
              <span className="text-ink">{u.parentName ?? '—'}</span>
            ) : (
              <span className="text-ink">Trực thuộc bạn</span>
            )}
          </Field>
          <Field label="Bảng giá">
            {u.priceList ? (
              <>
                <span className="text-ink">{u.priceList.name}</span>
                <span className="block font-mono text-xs text-muted">{u.priceList.code}</span>
              </>
            ) : (
              <span className="text-warn">Chưa có bảng giá</span>
            )}
            {canSetPrice && (
              <Button type="button" size="sm" variant="outline" className="mt-1.5 flex" onClick={() => setDlg({ kind: 'price' })}>
                {u.priceList ? 'Đổi bảng giá' : 'Gán bảng giá'}
              </Button>
            )}
          </Field>
          {u.tenantType === 'ctv' && u.scheme !== undefined && (
            <Field label="Bảng chiết khấu">
              {u.scheme ? (
                <>
                  <span className="text-ink">{u.scheme.name}</span>
                  <span className="block font-mono text-xs text-muted">{u.scheme.code}</span>
                </>
              ) : (
                <span className="text-warn">Chưa có bảng chiết khấu</span>
              )}
              {u.nextScheme && (
                <span className="mt-0.5 block text-xs text-inkSoft">
                  Từ tháng {monthLabel(u.nextScheme.effectiveFromMonth)}: {u.nextScheme.name}
                </span>
              )}
              {canSetScheme && (
                <Button type="button" size="sm" variant="outline" className="mt-1.5 flex" onClick={() => setDlg({ kind: 'scheme' })}>
                  {u.scheme || u.nextScheme ? 'Đổi bảng chiết khấu' : 'Gán bảng chiết khấu'}
                </Button>
              )}
            </Field>
          )}
          {u.tenantType !== 'shop' && u.workModes?.length === 1 && (
            <Field label="Giao diện">
              <span className="text-ink">{workModesLabel(u.workModes)}</span>
            </Field>
          )}
          {u.referredBy && (
            <Field label="Giới thiệu bởi">
              {inTree(u.referredBy.id) ? (
                <PickButton onClick={() => onPick(u.referredBy!.id)}>{u.referredBy.name}</PickButton>
              ) : (
                <span className="text-ink">{u.referredBy.name}</span>
              )}
            </Field>
          )}
          {!!u.referredCount && (
            <Field label="Đã giới thiệu">
              <span className="tabular-nums text-ink">{u.referredCount.toLocaleString('vi-VN')}</span>
            </Field>
          )}
        </dl>

        {/* Shop trực tiếp — chỉ chủ đơn vị. Khoá do nhà phân phối đặt thì không mở thay được */}
        {ownShop && (u.canResetPassword || u.status === 'active' || u.suspendedByParent) && (
          <div className="flex flex-wrap gap-2">
            {u.canResetPassword && (
              <Button type="button" size="sm" variant="outline" onClick={() => setDlg({ kind: 'password' })}>
                Đặt lại mật khẩu
              </Button>
            )}
            {u.status === 'active' ? (
              <Button type="button" size="sm" variant="outline" className="text-danger hover:text-danger" disabled={suspendM.isPending} onClick={askSuspend}>
                Tạm khóa
              </Button>
            ) : u.suspendedByParent ? (
              <Button type="button" size="sm" variant="outline" disabled={suspendM.isPending} onClick={() => suspendM.mutate(false)}>
                Mở khóa
              </Button>
            ) : null}
          </div>
        )}

        {node.children.length > 0 && (
          <div className="flex flex-col gap-0.5">
            <div className="pb-1 text-[13px] font-semibold text-inkSoft">Cấp dưới</div>
            {node.children.map((k) => (
              <button
                key={k.unit.id}
                type="button"
                onClick={() => onPick(k.unit.id)}
                className="motion-state flex min-h-11 items-center gap-2.5 rounded-[10px] px-2 text-left hover:bg-paperSoft"
              >
                <NetworkTypeBadge type={k.unit.tenantType} code={k.unit.code} className="h-[26px] min-w-[26px]" />
                <span className="min-w-0 flex-1 truncate text-[13.5px] font-medium text-ink">{k.unit.name}</span>
                <span className="shrink-0 text-xs text-muted">{downlineSub(k)}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {dlg?.kind === 'price' && (
        <AssignPriceDialog
          unit={u}
          current={u.priceList}
          options={(listsQ.data?.priceLists ?? []).filter((l) => l.kind === u.tenantType)}
          initialId={dlg.preset}
          canCreate={canPricing}
          onCreateNew={() => setDlg({ kind: 'newPrice' })}
          onClose={() => setDlg(null)}
        />
      )}
      {dlg?.kind === 'newPrice' && u.tenantType !== 'agency' && (
        <NewPriceListDialog kind={u.tenantType} onClose={() => setDlg({ kind: 'price' })} onCreated={(l) => setDlg({ kind: 'price', preset: l.id })} />
      )}
      {dlg?.kind === 'scheme' && (
        <AssignSchemeDialog
          ctv={{ shopId: u.id, name: u.name, scheme: u.scheme ?? null, nextScheme: u.nextScheme ?? null }}
          schemes={(schemesQ.data?.schemes ?? []).filter((x) => !x.archivedAt)}
          month={currentMonthVN()}
          hasAssignment={!!(u.scheme || u.nextScheme)}
          initialSchemeId={dlg.preset}
          onCreateNew={() => setDlg({ kind: 'newScheme' })}
          onClose={() => setDlg(null)}
          onAssigned={(r) => {
            toast.success(`Đã ${u.scheme ? 'đổi sang' : 'gán'} ${r.code} cho ${u.name} — áp dụng từ tháng ${monthLabel(r.effectiveFromMonth)}`)
            qc.invalidateQueries({ queryKey: ['network-units'] })
            qc.invalidateQueries({ queryKey: ['commissions'] })
            setDlg(null)
          }}
        />
      )}
      {dlg?.kind === 'newScheme' && <NewSchemeDialog onClose={() => setDlg({ kind: 'scheme' })} onCreated={(x) => setDlg({ kind: 'scheme', preset: x.id })} />}
      {dlg?.kind === 'password' && <ResetOwnerPasswordDialog unit={u} onClose={() => setDlg(null)} />}

      {(phone || ordersPath) && (
        <div className={cn('sticky bottom-0 grid gap-2 border-t border-rule bg-raise pb-4 pt-3', phone && ordersPath && 'grid-cols-[auto_minmax(0,1fr)]', padX)}>
          {ordersPath && (
            <Link
              to={`${ordersPath}?unit=${encodeURIComponent(u.id)}`}
              className="motion-state flex min-h-10 items-center justify-center rounded-[10px] border border-rule bg-raise px-3.5 text-[13.5px] font-semibold text-ink hover:bg-paperSoft mobile:min-h-11"
            >
              Xem đơn
            </Link>
          )}
          {phone && (
            <a
              href={`tel:${phone.replace(/\s/g, '')}`}
              className="motion-state flex min-h-10 items-center justify-center rounded-[10px] bg-accent px-3.5 text-[13.5px] font-semibold text-onAccent hover:bg-accentSoft mobile:min-h-11"
            >
              Gọi {phoneText(phone)}
            </a>
          )}
        </div>
      )}
    </>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-muted">{label}</dt>
      <dd className="min-w-0 [overflow-wrap:anywhere]">{children}</dd>
    </>
  )
}

function PickButton({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick} className="min-h-7 text-left font-semibold text-accent hover:underline mobile:min-h-11">
      {children}
    </button>
  )
}
